import React from 'react';
import { Box, Typography, LinearProgress, Card, CardContent, Button } from '@mui/material';
import { CheckCircle, HourglassEmpty, Error, Refresh } from '@mui/icons-material';
import { useApplicationManager } from '../../contexts/ApplicationManagerContext';
import { VERDE_MENTA, FUNDO_PAPEL, TEXTO_PRIMARIO, TEXTO_SECUNDARIO } from '../../themes/themeConstants';

/**
 * Componente de Loading robusto para toda a aplicação
 * Exibe o progresso real de inicialização baseado no ApplicationManager
 */
const ApplicationLoader: React.FC = () => {
  const { stage, error, retry, canRetry } = useApplicationManager();

  const stages = [
    { id: 'INITIALIZING', label: 'Iniciando aplicação', description: 'Verificando sistema' },
    { id: 'AUTHENTICATED', label: 'Autenticação verificada', description: 'Usuário autenticado' },
    { id: 'LOADING_PROJECTS', label: 'Carregando projetos', description: 'Buscando seus projetos' },
    { id: 'LOADING_PERMISSIONS', label: 'Carregando permissões', description: 'Verificando seus acessos' },
    { id: 'READY', label: 'Sistema pronto', description: 'Redirecionando...' }
  ];

  const getCurrentStageIndex = () => {
    return stages.findIndex(s => s.id === stage);
  };

  const getProgress = () => {
    const currentIndex = getCurrentStageIndex();
    return currentIndex >= 0 ? ((currentIndex + 1) / stages.length) * 100 : 0;
  };

  const getStageIcon = (stageId: string) => {
    const currentIndex = getCurrentStageIndex();
    const stageIndex = stages.findIndex(s => s.id === stageId);
    
    if (error) {
      return <Error sx={{ color: 'error.main' }} />;
    } else if (stageIndex < currentIndex || stage === 'READY') {
      return <CheckCircle sx={{ color: VERDE_MENTA }} />;
    } else if (stageIndex === currentIndex) {
      return <HourglassEmpty sx={{ color: VERDE_MENTA }} className="rotating" />;
    } else {
      return <HourglassEmpty sx={{ color: 'grey.500' }} />;
    }
  };

  const getCurrentStage = () => {
    return stages.find(s => s.id === stage);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: FUNDO_PAPEL,
        p: 3
      }}
    >
      <Card 
        sx={{ 
          minWidth: 400, 
          maxWidth: 500,
          backgroundColor: FUNDO_PAPEL,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          border: `1px solid ${VERDE_MENTA}30`
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: VERDE_MENTA, 
                fontWeight: 600,
                mb: 1
              }}
            >
              NeuroAgentes
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: TEXTO_SECUNDARIO }}
            >
              {error ? 'Erro na inicialização' : 'Inicializando sistema...'}
            </Typography>
          </Box>

          {/* Progress Bar */}
          {!error && (
            <Box sx={{ mb: 4 }}>
              <LinearProgress 
                variant="determinate" 
                value={getProgress()}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'grey.700',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: VERDE_MENTA,
                    borderRadius: 4,
                  }
                }}
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  textAlign: 'center', 
                  mt: 1, 
                  color: TEXTO_SECUNDARIO 
                }}
              >
                {Math.round(getProgress())}% completo
              </Typography>
            </Box>
          )}

          {/* Current Stage */}
          {!error && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: VERDE_MENTA,
                  fontWeight: 500,
                  mb: 1
                }}
              >
                {getCurrentStage()?.label || 'Processando...'}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: TEXTO_SECUNDARIO }}
              >
                {getCurrentStage()?.description || 'Aguarde...'}
              </Typography>
            </Box>
          )}

          {/* Stages List */}
          {!error && (
            <Box>
              {stages.map((stageItem, index) => (
                <Box
                  key={stageItem.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: index < stages.length - 1 ? 2 : 0,
                    opacity: getCurrentStageIndex() >= index || stage === 'READY' ? 1 : 0.5,
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  <Box sx={{ mr: 2 }}>
                    {getStageIcon(stageItem.id)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: TEXTO_PRIMARIO,
                        fontWeight: stage === stageItem.id ? 600 : 400
                      }}
                    >
                      {stageItem.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Error Display */}
          {error && (
            <Box sx={{ textAlign: 'center' }}>
              <Error sx={{ color: 'error.main', fontSize: 48, mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'error.main', mb: 2 }}>
                Erro na Inicialização
              </Typography>
              <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO, mb: 3 }}>
                {error}
              </Typography>
              {canRetry && (
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={retry}
                  sx={{
                    backgroundColor: VERDE_MENTA,
                    '&:hover': { backgroundColor: '#00A693' }
                  }}
                >
                  Tentar Novamente
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* CSS para animação de rotação */}
      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 2s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default ApplicationLoader;